import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, message } from 'antd';
import moment from 'moment';
import axios from 'axios';
import { Box } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
// Import Pusher instance from socket.js
import { pusher } from '../socket';

const { Text } = Typography;

const CallEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realtimeEventIds, setRealtimeEventIds] = useState(new Set()); // Track realtime events

  useEffect(() => {
    // Debug log for component mount
    console.log('CallEvents component mounted');

    // Subscribe to public calls channel
    const publicChannel = pusher.subscribe('public-calls');

    // Fetch initial events
    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/call-events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success && response.data.events) {
          console.log('Initial events loaded:', response.data.events);
          setEvents(response.data.events);
        } else {
          console.log('No initial events found');
          setEvents([]); // Set empty array if no events
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        console.log('Starting with empty events, will show real-time events only');
        setEvents([]); // Don't show error, just start with empty array
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();

    // Debug log for Pusher subscription
    console.log('Subscribing to public-calls channel...');

    // Handle call events from public channel only
    const handleCallEvent = (newEvent) => {
      console.log('Received public call-event:', newEvent);
      
      // Create a unique key for this realtime event
      const realtimeKey = `realtime-${newEvent.call_sid}-${newEvent.event_type}-${Date.now()}`;
      
      // Mark this event as realtime with a unique identifier
      setRealtimeEventIds(prev => {
        const updated = new Set(prev);
        updated.add(realtimeKey);
        console.log('Added realtime event:', realtimeKey);
        return updated;
      });
      
      setEvents(prevEvents => {
        // Find if there's an existing event with the same call_sid
        const existingEventIndex = prevEvents.findIndex(event => 
          event.call_sid === newEvent.call_sid
        );
        
        let updatedEvents;
        
        if (existingEventIndex !== -1) {
          const existingEvent = prevEvents[existingEventIndex];
          
          // Don't update if the new status shouldn't override the current status
          if (
            (existingEvent.status === 'completed' && ['in-progress', 'ringing'].includes(newEvent.status)) ||
            (existingEvent.status === 'no-answer' && newEvent.status === 'in-progress') ||
            (existingEvent.status === 'busy' && newEvent.status === 'in-progress')
          ) {
            return prevEvents;
          }
          
          // Update existing event and mark it as realtime
          updatedEvents = [...prevEvents];
          updatedEvents[existingEventIndex] = {
            ...existingEvent,
            ...newEvent,
            status: newEvent.status,
            event_type: newEvent.event_type,
            timestamp: newEvent.timestamp,
            isRealtime: true,
            realtimeKey: realtimeKey // Add unique identifier
          };
        } else {
          // Add as new event with realtime flag
          updatedEvents = [{
            ...newEvent,
            isRealtime: true,
            realtimeKey: realtimeKey // Add unique identifier
          }, ...prevEvents].slice(0, 100);
        }
        console.log('Updated events state:', updatedEvents);
        return updatedEvents;
      });
    };

    // Listen for real-time events from public channel
    publicChannel.bind('call-event', handleCallEvent);

    // Add connection state monitoring for public channel
    publicChannel.bind('pusher:subscription_succeeded', () => {
      console.log('Successfully subscribed to public-calls channel');
    });

    publicChannel.bind('pusher:subscription_error', (error) => {
      console.error('Public channel subscription error:', error);
      message.error('Public channel connection error');
    });

    return () => {
      console.log('Cleaning up Pusher subscriptions');
      // Cleanup public channel
      publicChannel.unbind('call-event');
      publicChannel.unbind('pusher:subscription_succeeded');
      publicChannel.unbind('pusher:subscription_error');
      pusher.unsubscribe('public-calls');
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'processing';
      case 'ringing':
        return 'warning';
      case 'failed':
        return 'error';
      case 'busy':
        return 'warning';
      case 'no-answer':
        return 'error';
      case 'canceled':
        return 'default';
      case 'initiated':
        return 'processing';
      default:
        return 'default';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'initiated':
        return 'blue';
      case 'ringing':
        return 'orange';
      case 'answered':
        return 'green';
      case 'completed':
        return 'cyan';
      case 'no-answer':
        return 'red';
      case 'busy':
        return 'orange';
      case 'canceled':
        return 'gray';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text, record) => {
        // Use created_at if timestamp is not available (for database records)
        const timeValue = text || record.created_at;
        const isRealtime = record.isRealtime || false;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text copyable>
              {timeValue ? moment(timeValue).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
            </Text>
            {isRealtime && (
              <Tag color="red" size="small" style={{ fontSize: '10px', fontWeight: 'bold' }}>
                🔴 LIVE
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (text, record) => {
        const isRealtime = record.isRealtime || false;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag color={getEventTypeColor(text)}>
              {text || 'N/A'}
            </Tag>
            {isRealtime && (
              <Tag color="volcano" size="small" style={{ fontWeight: 'bold' }}>
                NEW
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const isRealtime = record.isRealtime || false;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag color={getStatusColor(text)}>
              {text || 'N/A'}
            </Tag>
            {isRealtime && (
              <Tag color="green" size="small" style={{ fontWeight: 'bold' }}>
                ⚡ REALTIME
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'From',
      dataIndex: 'from_number',
      key: 'from_number',
      render: (text) => <Text copyable>{text || 'N/A'}</Text>
    },
    {
      title: 'To',
      dataIndex: 'to_number',
      key: 'to_number',
      render: (text) => <Text copyable>{text || 'N/A'}</Text>
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      render: (text) => (
        <Tag color={text === 'inbound' ? 'blue' : 'purple'}>
          {text || 'N/A'}
        </Tag>
      )
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (text) => text ? `${text}s` : 'N/A'
    },
    {
      title: 'Call SID',
      dataIndex: 'call_sid',
      key: 'call_sid',
      render: (text) => <Text copyable>{text}</Text>
    }
  ];

  return (
    <Card 
      title={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CallIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="h6" component="span" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            Real-time Call Events (Public Only)
          </Typography>
        </Box>
      }
      style={{ 
        margin: '0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e3e3e3'
      }}
      extra={
        <Tag color="#52c41a" icon={<span style={{ marginRight: 5 }}>🔴</span>}>
          Live Updates (Public Channel)
        </Tag>
      }
    >
      <Table 
        dataSource={events}
        columns={columns}
        rowKey={(record) => `${record.call_sid}-${record.event_type}-${record.timestamp}`}
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
        loading={loading}
        rowClassName={(record) => record.event_type?.toLowerCase() === 'completed' ? 'completed-row' : ''}
        style={{ 
          backgroundColor: '#fff',
          borderRadius: '8px'
        }}
      />
    </Card>
  );
};

export default CallEvents;